import { useQuery } from "@tanstack/react-query";

import type { SellerThreadSummary } from "@recruiter/shared";

import { SellerQueryError, threadListQueryOptions } from "../../lib/queries/seller";
import { useSellerStore } from "../../lib/store/seller-store";
import { SellerErrorState } from "./seller-error-state";

function statusPill(t: SellerThreadSummary): { label: string; cls: string } {
  if (t.has_offer || t.state === "offer_received") {
    return { label: "Offer sent", cls: "sl-pill--offer" };
  }
  if (t.state === "awaiting_seller" || t.state === "sent") {
    return { label: "Your reply", cls: "sl-pill--reply" };
  }
  if (t.state === "awaiting_buyer") {
    return { label: "With client", cls: "sl-pill--wait" };
  }
  if (t.state === "declined") {
    return { label: "Declined", cls: "sl-pill--wait" };
  }
  return { label: t.state, cls: "sl-pill--wait" };
}

// The active talent's thread list. Server state via TanStack Query (keyed by the
// active talent id); the Pusher dispatcher patches this cache entry as messages /
// thread updates land — no pulse refetch.
export function Inbox({
  openThreadId,
  onOpen,
}: {
  openThreadId: string | null;
  onOpen: (threadId: string) => void;
}) {
  const sellerId = useSellerStore((s) => s.activeSellerId);
  const { data, isPending, error, refetch } = useQuery(threadListQueryOptions(sellerId));
  const threads = data?.threads ?? [];

  if (isPending) {
    return <div className="sl-empty">Loading inbox…</div>;
  }
  if (error) {
    return (
      <SellerErrorState
        title="Couldn't load inbox"
        message={error instanceof SellerQueryError ? error.message : "Something went wrong."}
        onRetry={() => void refetch()}
      />
    );
  }
  if (threads.length === 0) {
    return (
      <div className="sl-empty">
        No briefs yet. When a client approves outreach to this talent, the concierge's message lands
        here.
      </div>
    );
  }

  return (
    <div className="sl-inbox">
      {threads.map((t) => {
        const pill = statusPill(t);
        return (
          <button
            key={t.thread_id}
            className="sl-thread-row"
            data-active={t.thread_id === openThreadId}
            onClick={() => onOpen(t.thread_id)}
          >
            {t.project_name ? (
              <div className="sl-thread-row__project">{t.project_name}</div>
            ) : null}
            <div className="sl-thread-row__top">
              <span className="sl-thread-row__client">{t.client_name}</span>
              <span className={`sl-pill ${pill.cls}`}>{pill.label}</span>
            </div>
            <div className="sl-thread-row__subject">{t.subject}</div>
            {t.last_message ? <div className="sl-thread-row__preview">{t.last_message}</div> : null}
          </button>
        );
      })}
    </div>
  );
}
